import { Box, Typography } from '@mui/material';
import React from 'react';
import { WhatsappEditTemplate } from 'types/templates';
import useLocalization from 'hooks/useLocalization';
import TemplatePreview from 'views/common/templates/single-template/TemplatePreview';
import { varsRegular } from 'utils/regexes';
import { TemplateVarsDataItem } from 'types/templates/templates-menu';

interface TemplatePreviewSidebarProps {
  selectedTemplate: WhatsappEditTemplate | null;
}

const TemplatePreviewSidebar: React.FC<TemplatePreviewSidebarProps> = ({ selectedTemplate }) => {
  const { currentLocale } = useLocalization();
  const localization = React.useMemo(() => currentLocale.settingsPopup.templates, [currentLocale]);

  // Build varsData from template variables and their default values
  const varsData = React.useMemo(() => {
    if (!selectedTemplate) return [];
    
    const data: TemplateVarsDataItem[] = [];
    
    // Handle body variables
    const bodyComponent = selectedTemplate.components?.find((comp) => comp.type === 'BODY');
    if (bodyComponent?.text) {
      const bodyVariables = bodyComponent.text.match(varsRegular) || [];
      bodyVariables.forEach((variable, index) => {
        const variableNumber = variable.match(/\d+/)?.[0];
        if (variableNumber) {
          const defaultValue = bodyComponent.example?.body_text?.[0]?.[index] || '';
          const paramName = `body_${variableNumber}` as any;
          data.push({
            name: paramName,
            label: `{{${variableNumber}}}`,
            options: null,
            required: false,
            type: 'text',
            text: defaultValue
          });
        }
      });
    }

    // Handle header variables
    const headerComponent = selectedTemplate.components?.find((comp) => comp.type === 'HEADER');
    if (headerComponent?.text) {
      const headerVariables = headerComponent.text.match(varsRegular) || [];
      headerVariables.forEach((variable, index) => {
        const variableNumber = variable.match(/\d+/)?.[0];
        if (variableNumber) {
          const defaultValue = headerComponent.example?.header_text?.[0] || '';
          const paramName = `header_${variableNumber}` as any;
          data.push({
            name: paramName,
            label: `{{${variableNumber}}}`,
            options: null,
            required: false,
            type: 'text',
            text: defaultValue
          });
        }
      });
    }

    // Handle footer variables
    const footerComponent = selectedTemplate.components?.find((comp) => comp.type === 'FOOTER');
    if (footerComponent?.text) {
      const footerVariables = footerComponent.text.match(varsRegular) || [];
      footerVariables.forEach((variable, index) => {
        const variableNumber = variable.match(/\d+/)?.[0];
        if (variableNumber) {
          const defaultValue = (footerComponent as any).example?.footer_text?.[0]?.[index] || '';
          const paramName = `footer_${variableNumber}` as any;
          data.push({
            name: paramName,
            label: `{{${variableNumber}}}`,
            options: null,
            required: false,
            type: 'text',
            text: defaultValue
          });
        }
      });
    }

    return data;
  }, [selectedTemplate]);

  return (
    <Box
      sx={{
        width: 280,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          mb: 0.25,
          '&:hover .MuiIconButton-root': {
            opacity: 1,
          },
          display: 'inline-block',
        }}
      >
        {selectedTemplate?.niceName || localization.list.preview.nameTitle}
      </Typography>

      <Typography
        variant="body2"
        sx={{ wordBreak: 'break-all', mb: 2.5 }}
        fontSize={13}
        color="text.secondary"
      >
        {selectedTemplate?.name ? selectedTemplate.name : localization.list.preview.nameDefaultValue}
      </Typography>

      {/* <TemplateMessagePreview selectedTemplate={selectedTemplate} /> */}
      {selectedTemplate && <TemplatePreview originalTemplate={selectedTemplate} varsData={varsData} noUpload={true} />}
    </Box>
  );
};

export default TemplatePreviewSidebar;
